import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useFirestore } from '../hooks/useFirestore';
import { useAuth } from '../hooks/useAuth';
import { Pizza, Pizzeria, Location, MapCenter } from '../types';
import Map from '../components/Map';
import { geocodeAddress } from '../utils/geocoding';

export default function PizzaDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { getPizza, getPizzeria } = useFirestore();
  const [pizzaData, setPizzaData] = useState<Pizza | null>(null);
  const [pizzerias, setPizzerias] = useState<Pizzeria[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapLocations, setMapLocations] = useState<Location[]>([]);

  useEffect(() => {
    const loadPizzaData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (id) {
          const data = await getPizza(id);
          if (data) {
            setPizzaData(data);
            
            const pizzeriaIds = data.pizzerias || [];
            if (pizzeriaIds.length > 0) {
              const pizzeriaPromises = pizzeriaIds.map(pizzeriaId => 
                getPizzeria(pizzeriaId)
              );
              const pizzeriaResults = await Promise.all(pizzeriaPromises);
              const validPizzerias = pizzeriaResults.filter((p): p is Pizzeria => p !== null);
              setPizzerias(validPizzerias);

              const locations = (await Promise.all(
                validPizzerias.map(async (pizzeria) => {
                  if (pizzeria.lat && pizzeria.lng) {
                    return {
                      id: pizzeria.id,
                      lat: pizzeria.lat,
                      lng: pizzeria.lng,
                      name: pizzeria.name
                    } as Location;
                  }
                  
                  const coordinates = await geocodeAddress(pizzeria.address, pizzeria.city);
                  if (coordinates) {
                    return {
                      id: pizzeria.id,
                      lat: coordinates.lat,
                      lng: coordinates.lng,
                      name: pizzeria.name
                    } as Location;
                  }
                  return null;
                })
              )).filter((loc): loc is Location => loc !== null);

              setMapLocations(locations);
            }
          } else {
            setError('Pizza non trovata');
          }
        }
      } catch (err: any) {
        console.error('Error loading pizza:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPizzaData();
  }, [id, getPizza, getPizzeria]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="container mx-auto px-4 py-8">
          <div>Caricamento...</div>
        </div>
      </div>
    );
  }

  if (error || !pizzaData) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="container mx-auto px-4 py-8">
          <div className="text-red-600">{error || 'Pizza non trovata'}</div>
        </div>
      </div>
    );
  }

  const getMapCenter = (): MapCenter => {
    if (mapLocations.length > 0) {
      return {
        lat: mapLocations[0].lat,
        lng: mapLocations[0].lng,
        zoom: 13
      };
    }
    return {
      lat: 41.9028,
      lng: 12.4964,
      zoom: 6
    };
  };

  console.log('Pizza detail map center:', getMapCenter());

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-pizzaveg hover:text-green-700 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Torna alla Home
          </button>
        </div>

        <div className="space-y-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {pizzaData?.imageUrl && (
                <div className="h-64 lg:h-full">
                  <img
                    src={pizzaData.imageUrl}
                    alt={pizzaData.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="p-6">
                <h1 className="text-3xl font-bold text-gray-900">{pizzaData?.name}</h1>
                <div className="text-sm text-pizzaveg font-medium mt-1">
                  {pizzaData?.isVegan ? 'Pizza Vegana' : 'Pizza Vegetariana'}
                </div>
                
                <p className="mt-4 text-gray-600">{pizzaData?.description}</p>
                
                <div className="mt-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Ingredienti</h2>
                  <ul className="grid grid-cols-2 gap-2">
                    {pizzaData?.ingredients.map((ingredient, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <span className="text-pizzaveg mr-2">•</span>
                        {ingredient}
                      </li>
                    ))}
                  </ul>
                </div>

                {pizzaData?.price && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Prezzo</span>
                      <span className="text-2xl font-bold text-pizzaveg">
                        € {pizzaData.price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Dove puoi trovarla
                </h2>
                <div className="space-y-3">
                  {pizzerias.map(pizzeria => (
                    <Link
                      key={pizzeria.id}
                      to={`/pizzerie/${pizzeria.id}`}
                      className="block bg-gray-50 hover:bg-gray-100 rounded-lg p-3 transition duration-150"
                    >
                      <div className="font-medium text-pizzaveg">
                        {pizzeria.name}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {pizzeria.address}, {pizzeria.city}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {mapLocations.length > 0 && (
              <div className="lg:col-span-7">
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Visualizza sulla mappa
                  </h2>
                  <div className="h-[400px] rounded-lg overflow-hidden">
                    <Map
                      locations={mapLocations}
                      center={getMapCenter()}
                      onMarkerClick={(id) => navigate(`/pizzerie/${id}`)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}